/**
 * Client/SSR helper for routing raw PromoTix S3 event images through the self-hosted
 * image optimizer (`/img`, see server/api/imageOptimizer.js).
 *
 * Only URLs on the PromoTix S3 bucket are rewritten; everything else (Sharetribe imgix
 * variants, local /static assets, the default image) is returned untouched. The whole
 * feature is gated behind REACT_APP_IMAGE_OPTIMIZER so production keeps serving raw S3
 * until the flag is flipped (staging-first rollout).
 *
 * REACT_APP_* is inlined into the client bundle at build time and is also available to
 * the SSR server at runtime, so the same gate works on both sides.
 */

// Served under /static so it reuses CloudFront's /static/* CachingOptimized behavior.
// All params live in the path (CachingOptimized ignores query strings):
//   /static/img/<width>/<base64url(original)>.webp
const OPTIMIZER_PATH_PREFIX = '/static/img';
const ALLOWED_HOST = 'promotix.s3.us-east-1.amazonaws.com';

// base64url-encode the source URL. Buffer on the SSR server (UTF-8 safe), btoa in the
// browser. For ASCII S3 URLs the two produce identical output, so there is no
// SSR/hydration mismatch. Returns '' on failure so the caller can fall back to the original.
const toBase64Url = str => {
  try {
    const b64 =
      typeof Buffer !== 'undefined' && Buffer.from
        ? Buffer.from(str, 'utf8').toString('base64')
        : btoa(str);
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  } catch (e) {
    return '';
  }
};

/** Default responsive widths for listing/event cards (1x + retina). */
export const CARD_IMAGE_WIDTHS = [240, 400, 800];

export const isImageOptimizerEnabled = () =>
  process.env.REACT_APP_IMAGE_OPTIMIZER === 'true';

/** True only for raw PromoTix S3 object URLs that we should optimize. */
export const isOptimizableImageUrl = url => {
  if (typeof url !== 'string' || url === '') {
    return false;
  }
  try {
    const parsed = new URL(url);
    return (
      (parsed.protocol === 'https:' || parsed.protocol === 'http:') &&
      parsed.host === ALLOWED_HOST
    );
  } catch (e) {
    return false;
  }
};

/**
 * Rewrite a single eligible URL to the optimizer at the given width.
 * Returns the original URL unchanged when disabled or ineligible.
 */
export const optimizeImageUrl = (url, width) => {
  if (!isImageOptimizerEnabled() || !isOptimizableImageUrl(url)) {
    return url;
  }
  const encoded = toBase64Url(url);
  if (!encoded) {
    return url;
  }
  const w = Number.parseInt(width, 10);
  const safeWidth = Number.isFinite(w) && w > 0 ? w : 800;
  return `${OPTIMIZER_PATH_PREFIX}/${safeWidth}/${encoded}.webp`;
};

/**
 * Build `{ src, srcSet }` for a single original image URL.
 *
 * @param {string} url original image URL
 * @param {object} [opts]
 * @param {number[]} [opts.widths] candidate widths for srcSet
 * @param {number} [opts.fallbackWidth] width used for the `src` attribute
 * @returns {{ src: string, srcSet: (string|undefined) }}
 */
export const optimizedImageSources = (url, opts = {}) => {
  const widths = Array.isArray(opts.widths) && opts.widths.length ? opts.widths : CARD_IMAGE_WIDTHS;
  const fallbackWidth = opts.fallbackWidth || widths[Math.floor(widths.length / 2)] || widths[0];

  if (!isImageOptimizerEnabled() || !isOptimizableImageUrl(url)) {
    return { src: url, srcSet: undefined };
  }

  const srcSet = widths
    .map(w => `${optimizeImageUrl(url, w)} ${w}w`)
    .join(', ');

  return { src: optimizeImageUrl(url, fallbackWidth), srcSet };
};
